/**
 * ============================================================
 * MONTE HERMOM | MENU PARADIGMA LATERAL
 * ============================================================
 * ARQUIVO: /src/components/perfil/MenuParadigma_lateral.jsx
 * VERSÃO:  3.0.0 (compacto + lucide-react)
 *
 * MUDANÇAS v3.0.0:
 *   ✔ Largura: 384px → 260px
 *   ✔ Padding dos itens: p-5 → px-3 py-2
 *   ✔ Border-radius: 2.5rem → 0.75rem
 *   ✔ Ícones: <i data-lucide> → <Icon /> do lucide-react
 *   ✔ Sub-menus compactos
 *   ✔ Altura total reduzida em ~45%
 * ============================================================
 */

import React from 'react';
import {
  LayoutGrid,
  FilePlus2,
  Search,
  Calendar,
  ListChecks,
  Banknote,
  Database,
  MessageSquareShare,
  LogOut,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

import { useAuthSaaS } from '../../context/AuthSaaSContext';
import Perfil_Card_soberano from './Perfil_Card_soberano';

/* ---------- Definição dos menus ---------- */

const MENU_PRINCIPAL = [
  { id: 'DASHBOARD',         label: 'Dashboard',            icon: LayoutGrid },
  { id: 'REQUERENTE_INICIAL',label: 'Novo Laudo',           icon: FilePlus2, destaque: true },
];

const MENU_MEIO = [
  { id: 'PESQUISA_GLOBAL',   label: 'Pesquisa Global',      icon: Search },
  { id: 'AGENDA',            label: 'Agenda de Vistorias',  icon: Calendar },
  { id: 'TAREFAS',           label: 'Tarefas Operacionais', icon: ListChecks },
];

const SUBMENU_FINANCEIRO = [
  'ORCAMENTOS', 'CONTAS_A_RECEBER', 'CONTAS_A_PAGAR', 'FLUXO_CAIXA', 'CREDITOS',
];

const SUBMENU_CADASTROS = [
  'USUARIOS', 'GRUPOS', 'CARTORIOS', 'FORUM', 'IMOVEIS_PARADIGMA',
];

/* =========================
   COMPONENTE
========================= */

const MenuParadigmaLateral = ({
  abaAtiva,
  setAbaAtiva,
  menuFinanceiro,
  setMenuFinanceiro,
  menuCadastros,
  setMenuCadastros,
}) => {
  const { usuario, logout } = useAuthSaaS();

  const userData = {
    nome: usuario?.nome || 'Usuário',
    cargo: usuario?.tenant_tipo || usuario?.nivel_acesso || 'PERITO',
    pontos: usuario?.pontos || 0,
    abertos: 0,
    concluidos: 0,
    revisao: 0,
    social: false,
    identidade: false,
    profissional: false,
    documental: false,
    auth2fa: false,
  };

  const tenantData = {
    nome: usuario?.empresa_nome || 'Monte Hermom',
    id: usuario?.empresa_id || 1,
  };

  const handleLogout = async () => {
    if (!confirm('Deseja realmente sair do sistema?')) return;
    await logout();
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <aside className="w-64 bg-[#020617] border-r border-slate-800 flex flex-col overflow-hidden">

      {/* ---------- PERFIL (compacto) ---------- */}
      <div className="p-3 border-b border-slate-800">
        <Perfil_Card_soberano userData={userData} tenantData={tenantData} />
      </div>

      {/* ---------- NAVEGAÇÃO ---------- */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5 scrollbar-thin">

        {/* Bloco 1: principal */}
        {MENU_PRINCIPAL.map(item => (
          <MenuItem
            key={item.id}
            item={item}
            ativo={abaAtiva === item.id}
            onClick={() => setAbaAtiva(item.id)}
          />
        ))}

        {/* Divisor */}
        <div className="h-px bg-slate-800/60 my-2 mx-2" />

        {/* Bloco 2: ferramentas */}
        {MENU_MEIO.map(item => (
          <MenuItem
            key={item.id}
            item={item}
            ativo={abaAtiva === item.id}
            onClick={() => setAbaAtiva(item.id)}
          />
        ))}

        {/* Bloco 3: Financeiro (colapsável) */}
        <CollapsibleGroup
          titulo="Financeiro"
          icon={Banknote}
          aberto={menuFinanceiro}
          onToggle={() => setMenuFinanceiro(!menuFinanceiro)}
          subitems={SUBMENU_FINANCEIRO}
          ativoAtual={abaAtiva}
          onSelect={setAbaAtiva}
        />

        {/* Bloco 4: Cadastros (colapsável) */}
        <CollapsibleGroup
          titulo="Cadastros Base"
          icon={Database}
          aberto={menuCadastros}
          onToggle={() => setMenuCadastros(!menuCadastros)}
          subitems={SUBMENU_CADASTROS}
          ativoAtual={abaAtiva}
          onSelect={setAbaAtiva}
        />

        {/* Bloco 5: Colaboração */}
        <MenuItem
          item={{ id: 'COLABORACAO', label: 'Colaboração', icon: MessageSquareShare }}
          ativo={abaAtiva === 'COLABORACAO'}
          onClick={() => setAbaAtiva('COLABORACAO')}
        />

      </nav>

      {/* ---------- SAIR ---------- */}
      <div className="p-3 border-t border-slate-800">
        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg
                     text-rose-500 hover:text-rose-400 hover:bg-rose-950/40
                     font-black text-[10px] uppercase tracking-wider
                     transition-colors"
        >
          <LogOut size={14} />
          Sair do sistema
        </button>
      </div>
    </aside>
  );
};

/* =========================
   SUB-COMPONENTE: ITEM
========================= */

function MenuItem({ item, ativo, onClick }) {
  const Icon = item.icon;

  const baseClass =
    'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg ' +
    'font-black text-[10px] uppercase tracking-wider ' +
    'transition-all text-left';

  // Item em destaque (Novo Laudo) tem cor própria
  if (item.destaque) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${baseClass} border ${
          ativo
            ? 'bg-rose-900 border-rose-800 text-white'
            : 'border-rose-900/40 text-rose-500 hover:bg-rose-950/40 hover:text-rose-300'
        }`}
      >
        <Icon size={14} />
        <span className="truncate">{item.label}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClass} ${
        ativo
          ? 'bg-white text-slate-950 shadow-md'
          : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
      }`}
    >
      <Icon size={14} className={ativo ? '' : 'opacity-70'} />
      <span className="truncate">{item.label}</span>
    </button>
  );
}

/* =========================
   SUB-COMPONENTE: GRUPO COLAPSÁVEL
========================= */

function CollapsibleGroup({
  titulo,
  icon: Icon,
  aberto,
  onToggle,
  subitems,
  ativoAtual,
  onSelect,
}) {
  const algumAtivoNoGrupo = subitems.includes(ativoAtual);

  return (
    <div>
      {/* Botão do grupo */}
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between gap-2.5 px-3 py-2 rounded-lg
                    font-black text-[10px] uppercase tracking-wider transition-all text-left ${
          algumAtivoNoGrupo
            ? 'text-white'
            : aberto
              ? 'text-slate-200'
              : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
        }`}
      >
        <span className="flex items-center gap-2.5 min-w-0">
          <Icon size={14} className="opacity-70 shrink-0" />
          <span className="truncate">{titulo}</span>
        </span>
        {aberto
          ? <ChevronDown size={12} className="shrink-0 opacity-60" />
          : <ChevronRight size={12} className="shrink-0 opacity-60" />}
      </button>

      {/* Sub-itens */}
      {aberto && (
        <div className="mt-0.5 ml-4 pl-3 border-l border-slate-800 space-y-0.5">
          {subitems.map(id => {
            const ativo = ativoAtual === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onSelect(id)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md
                            font-black text-[9px] uppercase tracking-wider
                            transition-all text-left ${
                  ativo
                    ? 'text-rose-400 bg-rose-950/30'
                    : 'text-slate-500 hover:text-slate-200 hover:bg-slate-800/40'
                }`}
              >
                <span className={`w-1 h-1 rounded-full shrink-0 ${
                  ativo ? 'bg-rose-400' : 'bg-slate-600'
                }`} />
                <span className="truncate">{id.replace(/_/g, ' ')}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MenuParadigmaLateral;